import { useCallback, useEffect, useState } from 'react';
import { getSentAtTimestamp } from 'App/Tracking/serverside';
import { tracking } from 'App/Tracking';
import { logException } from 'App/logging';
import { conversationMessagesConversationMessagesGet as getConversationHistory } from 'Assistant/__generated__/client';
import {
  ConversationMessagePagination,
  ConversationMessagesConversationMessagesGetParams as ConversationQueryParameters,
  ConversationStreamingSuccess,
  GenerateResponseConversationPostParams,
  ConversationInput,
  ChatbotKey,
  MappedMessageType,
} from 'Assistant/__generated__/client.schemas';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { multiDestinationLogger } from 'App/Tracking/multiDestinationLogger';
import { usePendingMessage } from 'Assistant/PendingMessage';
import type {
  ConversationStreamingFrame,
  ChatbotMessageSentSource,
  ConversationUpdateType,
  HistoryErrorType,
  Message,
  UserMessage,
} from '../types';
import { ChatbotMessageRole } from '../enums/ChatbotMessageRole';
import { textDecoder } from '../utils/textEncodings';
import {
  mapHistoryErrorType,
  mapHistoryToMessages,
  mapMessageToInput,
} from '../utils/mapConversationHistory';
import { isHTTPValidationError } from '../utils/isValidationError';
import { mapAssistantPayloadToSource } from '../utils/mapAssistantPayloadToSource';
import { convertObjToQueryString } from '../utils/convertObjToQueryString';
import { USER_QUERY } from '../graphql';
import { User } from '../graphql/__generated__/User';
import { useAuthHeader } from './useAuthHeader';
import { useStaticWelcomeIntro } from './useStaticWelcomeIntro';

const { REACT_APP_CHAT_API_URL } = process.env;

export type ConversationHistory = {
  isLoading: boolean;
  hadError: boolean;
  messages: Message[];
} & Pick<ConversationMessagePagination, 'hasPrevious' | 'startCursor'>;

export type ConversationHistoryOutput = {
  messages: Message[];
  conversationId?: string;
  isLoadingHistory: boolean;
  isSubmittingMessage: boolean;
  isStreamingMessage: boolean;
  hadErrorSubmittingMessage: boolean;
  hadErrorLoadingHistory: HistoryErrorType;
  transparencyCheckRequired: boolean;
  submitMessage(
    message: UserMessage,
    source?: ChatbotMessageSentSource,
  ): Promise<void>;
  resubmitMessages(): Promise<void>;
  loadMoreMessages(): Promise<void>;
  retryLoadingHistory(): Promise<void>;
  submitFollowUpMessage(): Promise<void>;
  submitUrlPromptMessage(args: {
    encodedPrompt: string;
    encodedSignature: string;
  }): Promise<void>;
  handleAcknowledgeTransparencyCheck(): void;
  clearConversationHistory(): void;
};

type ChatbotTextMessage = {
  value: string;
  type: string;
};

export type FollowUp = {
  type: 'command';
  subtype: 'follow_up';
  type_discriminator: 'command:follow_up';
};

export type UrlPromptCommand = {
  type: 'command';
  subtype: 'url_prompt';
  encoded_prompt: string;
  encoded_signature: string;
};

export type CommandMessage = FollowUp | UrlPromptCommand;

export type AssistantStreamMessage = {
  id: string;
  content: [ChatbotTextMessage];
};

type UserConversationHistoryParams = {
  onConversationUpdated?(updateType: ConversationUpdateType): void;
  initialChunkSize?: number;
  /**
   * The chatbot to pin for the whole session. If the feature flag is enabled,
   * set to `ChatbotKey.default` when the user arrived with clear user intent
   * (conversation starter, home chat input, or url-prompt deep link);
   * left `undefined` when they arrived with no intent
   * (nav menu / direct link) so the Chat API can choose the initiator
   * (Welcome Conversation or generic follow-up). Applied to every message sent.
   */
  chatModelId?: ChatbotKey;
  /**
   * Whether the server-driven welcome conversation is enabled. When disabled,
   * no follow-up is initiated, so acknowledging the transparency check seeds a
   * static introduction (name / no_name variant) to avoid a blank screen.
   */
  welcomeConversationEnabled?: boolean;
  /**
   * Whether the session started from explicit user intent (conversation
   * starter, home chat input, or url-prompt deep link). Those sessions already
   * produce a first message, so no static introduction is seeded for them.
   */
  hasUserIntent?: boolean;
};

const CONVERSATION_CHUNK_SIZE = 20;

function isFollowUp(message: Message | CommandMessage): message is FollowUp {
  return 'subtype' in message && message.subtype === 'follow_up';
}

function isUrlPrompt(
  message: Message | CommandMessage,
): message is UrlPromptCommand {
  return 'subtype' in message && message.subtype === 'url_prompt';
}

function getMessageRole(
  message: Message | CommandMessage,
): 'follow_up' | 'url_prompt' | 'user' {
  if (isFollowUp(message)) return 'follow_up';
  if (isUrlPrompt(message)) return 'url_prompt';

  return 'user';
}

function findLastUserMessageIndex(messages: Message[]): number {
  return messages.findLastIndex(
    message => message.role === ChatbotMessageRole.user,
  );
}

export function isCommandMessage(
  message: Message | CommandMessage,
): message is CommandMessage {
  return 'subtype' in message;
}

export function useConversationHistory({
  onConversationUpdated,
  initialChunkSize,
  chatModelId,
  welcomeConversationEnabled,
  hasUserIntent,
}: UserConversationHistoryParams): ConversationHistoryOutput {
  const [history, setHistory] = useState<ConversationHistory>({
    isLoading: true,
    hadError: false,
    messages: [],
    hasPrevious: true,
  });
  const [conversationId, setConversationId] = useState<string>();
  const [isSubmittingMessage, setIsSubmittingMessage] =
    useState<boolean>(false);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [isSubmittingError, setIsSubmittingError] = useState<boolean>(false);
  const [featureUnavailable, setFeatureUnavailable] = useState<boolean>(false);
  const [userSessionExpired, setUserSessionExpired] = useState<boolean>(false);
  const [transparencyCheckRequired, setTransparencyCheckRequired] =
    useState<boolean>(history.messages.length === 0);
  const [pendingCommandMessage, setPendingCommandMessage] =
    useState<CommandMessage | null>(null);
  const accessTokenHeader = useAuthHeader();
  const { clearPendingMessage } = usePendingMessage();

  const { t: translate } = useTranslation('chatbot');

  const { data: userData } = useQuery<User>(USER_QUERY);

  const buildIntroMessage = useCallback((): Message => {
    const firstName = userData?.user?.firstName;

    return {
      id: 'assistant-intro',
      source: [
        {
          kind: 'text',
          value: translate('conversation.chatbot_starter.introduction', {
            name: firstName,
            context: firstName ? '' : 'no_name',
          }),
        },
      ],
      role: ChatbotMessageRole.assistant,
      createdAt: new Date().toISOString(),
      type: MappedMessageType.text,
    };
  }, [userData, translate]);

  const fetchConversationHistory = useCallback(
    async (params: ConversationQueryParameters) => {
      // Don't refetch history when conversation ID is set initially (Indicated by repeat call with no before param)
      if (!accessTokenHeader || (conversationId && !params.before)) {
        return;
      }

      try {
        setHistory(prevHistory => ({
          ...prevHistory,
          isLoading: true,
        }));

        const response = await getConversationHistory(
          { ...params, multi_modal: true },
          {
            headers: {
              'client-platform': 'web',
              ...accessTokenHeader,
            },
          },
        );

        /**
         * We handle validation based errors here as they are
         * not handled by the fetch function.
         */
        if (response.status === 401) {
          setUserSessionExpired(true);

          return;
        }
        if (response.status === 422) {
          const body = response.data;
          if (isHTTPValidationError(body) && body.detail.length > 0) {
            logException(new Error(`Validation error: ${body.detail[0].msg}`), {
              tags: {
                product: 'nova',
                feature: 'nova-chat',
                action: 'fetch-conversation-history',
                errorType: 'validation',
              },
              extra: {
                validationErrors: body.detail,
                requestParams: params,
                conversationId,
                endpoint: 'getConversationHistory',
              },
            });
            setFeatureUnavailable(true);

            return;
          }
        }

        if (response.status !== 200) {
          throw new Error(`Request Error: ${response.status}`);
        }

        const { data } = response;
        if (data.conversation_id && conversationId !== data.conversation_id) {
          setConversationId(data.conversation_id);
        }

        const mapped = data.items.flatMap(mapHistoryToMessages);

        // If the user already has messages, we no longer need to check for transparency
        if (mapped.length > 0 && transparencyCheckRequired) {
          setTransparencyCheckRequired(false);
        }

        setHistory(prevHistory => ({
          ...prevHistory,
          messages: [...mapped, ...prevHistory.messages],
          isLoading: false,
          hadError: false,
          hasPrevious: data.hasPrevious,
          startCursor: data.startCursor,
        }));
      } catch (ex) {
        logException(ex, {
          tags: {
            product: 'nova',
            feature: 'nova-chat',
            action: 'fetch-conversation-history',
          },
          extra: {
            conversationId,
            messageCount: history.messages.length,
            hadPreviousError: history.hadError,
            chunkSize: params.amount,
            beforeCursor: params.before,
            hasPrevious: history.hasPrevious,
          },
        });
        setHistory(prevHistory => ({
          ...prevHistory,
          isLoading: false,
          hadError: true,
        }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessTokenHeader, conversationId],
  );

  const handleStreamingChunk = (chunk: ConversationStreamingSuccess) => {
    if (!chunk.content.value || !chunk.id) {
      return;
    }

    const assistantSource = mapAssistantPayloadToSource(chunk.content);
    if (!assistantSource) return;

    setHistory(prev => {
      const messages = [...prev.messages];
      const idx = messages.findIndex(
        m => m.role === ChatbotMessageRole.assistant && m.id === chunk.id,
      );
      if (idx === -1) {
        messages.push({
          id: chunk.id,
          role: ChatbotMessageRole.assistant,
          createdAt: new Date().toISOString(),
          source: [assistantSource],
        } as Message);
      } else {
        const existing = messages[idx] as Extract<
          Message,
          { role: ChatbotMessageRole.assistant }
        >;
        const existingChunks = [...existing.source];
        // merge consecutive text
        const last = existingChunks[existingChunks.length - 1];
        if (last && last.kind === 'text' && assistantSource.kind === 'text') {
          last.value += assistantSource.value;
        } else {
          existingChunks.push(assistantSource);
        }
        messages[idx] = { ...existing, source: existingChunks };
      }

      return { ...prev, messages };
    });
  };

  // Puts the chat into the retryable error state shared by every submit failure.
  const showSubmitError = (
    message: Message | CommandMessage,
    streamedMessageIds: Set<string>,
  ) => {
    // The failed response is not kept, so drop any part of it already shown.
    // For a user message this also leaves it last, so a retry re-sends it.
    setHistory(prev => ({
      ...prev,
      messages: prev.messages.filter(
        m =>
          m.role !== ChatbotMessageRole.assistant ||
          !streamedMessageIds.has(m.id),
      ),
    }));
    // If stream has errored set isStreaming to false
    setIsStreaming(false);
    // Remove loading animation
    setIsSubmittingMessage(false);
    setIsSubmittingError(true);
    // Command messages (the follow-up greeting, url_prompt deep links) are
    // never added to history, so hold on to the failed one — retrying re-sends
    // it rather than the latest conversation message.
    if (isCommandMessage(message)) {
      setPendingCommandMessage(message);
    }
    // Update the scroll view upon each new chunk of message added to the state
    onConversationUpdated?.('NEW_MESSAGE');
  };

  const submitChatbotMessage = async (
    message: Message | CommandMessage,
    params: GenerateResponseConversationPostParams,
  ): Promise<void> => {
    // Assistant messages streamed by this submit, so a failed response can be removed.
    const streamedMessageIds = new Set<string>();

    try {
      setIsSubmittingMessage(true);
      // A new submit supersedes any error still showing from a previous attempt.
      setIsSubmittingError(false);
      setPendingCommandMessage(null);

      const conversationInput: ConversationInput = {
        message: mapMessageToInput(message),
        metadata: {
          clientSentAtUtcTimestamp: getSentAtTimestamp(),
        },
      };

      const queryString = convertObjToQueryString({
        ...params,
        multi_modal: true,
      });
      const url = `${REACT_APP_CHAT_API_URL}/conversation${
        queryString ? `?${queryString}` : ''
      }`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'client-platform': 'web',
          ...accessTokenHeader,
        },
        body: JSON.stringify(conversationInput),
      });

      if (!response.body) {
        throw new Error('No response body');
      }

      setIsStreaming(true);

      const reader = response.body.getReader();
      let done = false;

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        if (value && value.byteLength > 0) {
          const chunk = textDecoder(value);
          // Process the chunk
          const decodedChunks = chunk.split('\n');
          for (const decodedChunk of decodedChunks) {
            if (chunk && decodedChunk.trim().length > 0) {
              const chunkMessage: ConversationStreamingFrame =
                JSON.parse(decodedChunk);

              /**
               * We handle validation based errors here as they are
               * not handled by the fetch function. These errors still
               * come via stream so need to be decoded and handled.
               */
              if (response.status === 401) {
                setIsSubmittingMessage(false);
                setIsStreaming(false);
                setUserSessionExpired(true);

                return;
              }
              if (
                response.status === 422 &&
                isHTTPValidationError(chunkMessage) &&
                chunkMessage.detail.length > 0
              ) {
                logException(
                  new Error(
                    `Streaming validation error: ${chunkMessage.detail[0].msg}`,
                  ),
                  {
                    tags: {
                      product: 'nova',
                      feature: 'nova-chat',
                      action: 'submit-message-streaming',
                      errorType: 'validation',
                    },
                    extra: {
                      validationErrors: chunkMessage.detail,
                      conversationId,
                      messageRole: getMessageRole(message),
                      messageCount: history.messages.length,
                    },
                  },
                );
                setIsSubmittingMessage(false);
                throw chunkMessage.detail[0].msg;
              } else if (chunkMessage.type === 'error') {
                // The error frame is always the last line, and Chat API has
                // already logged the failure, so show the error without
                // reporting it to Sentry again.
                showSubmitError(message, streamedMessageIds);

                return;
              } else {
                setIsSubmittingMessage(false);
                streamedMessageIds.add(chunkMessage.id);
                handleStreamingChunk(chunkMessage);
              }
            }
          }
        }
      }
      // Once stream is complete set isStreaming to false
      setIsStreaming(false);

      // The message completed without erroring — whether or not nova returned
      // anything — so any pending message notification is no longer relevant.
      // Errors deliberately leave the badge in place, so a retry can clear it.
      clearPendingMessage();

      // Usually we set isSubmittingMessage to false on receiving a response
      // from nova in the message history request, but these messages don't
      // necessarily trigger a response.
      if (isFollowUp(message) || isUrlPrompt(message)) {
        setIsSubmittingMessage(false);
      }
    } catch (ex) {
      logException(ex, {
        tags: {
          product: 'nova',
          feature: 'nova-chat',
          action: 'submit-message-streaming',
        },
        extra: {
          conversationId,
          messageRole: getMessageRole(message),
          wasStreaming: isStreaming,
          messageCount: history.messages.length,
          chatModelId: params.chat_model_id,
        },
      });
      showSubmitError(message, streamedMessageIds);
    }
  };

  useEffect(() => {
    void fetchConversationHistory({
      amount: initialChunkSize || CONVERSATION_CHUNK_SIZE,
    });
  }, [fetchConversationHistory, initialChunkSize]);

  useStaticWelcomeIntro({
    welcomeConversationEnabled,
    hasUserIntent,
    transparencyCheckRequired,
    isLoadingHistory: history.isLoading,
    buildIntroMessage,
    setHistory,
  });

  const submitFollowUpMessage = async () => {
    tracking.track('ai-coach-chatbot-followup-message-sent', {
      source: 'ai-coach-chatbot-ui',
      conversationId,
    });

    await submitChatbotMessage(
      {
        type: 'command',
        subtype: 'follow_up',
        type_discriminator: 'command:follow_up',
      },
      {
        chat_model_id: chatModelId,
      },
    );
  };

  const submitUrlPromptMessage: ConversationHistoryOutput['submitUrlPromptMessage'] =
    async ({ encodedPrompt, encodedSignature }) => {
      void multiDestinationLogger({
        eventName: 'ai-coach-chatbot-url-message-sent',
        properties: {
          source: 'email-deep-link',
          conversationId,
        },
      });

      await submitChatbotMessage(
        {
          type: 'command',
          subtype: 'url_prompt',
          encoded_prompt: encodedPrompt,
          encoded_signature: encodedSignature,
        },
        {
          chat_model_id: chatModelId,
        },
      );
    };

  const submitMessage: ConversationHistoryOutput['submitMessage'] = async (
    message,
    source = 'conversation-chat-input',
  ) => {
    onConversationUpdated?.('NEW_MESSAGE');
    const newMessage: Message = { ...message, role: ChatbotMessageRole.user };

    /**
     * There is a server side event which better tracks user messages
     * sent to the bot. This is an extra client side event so data can
     * be easily surfaced through amplitude.
     */
    void multiDestinationLogger({
      eventName: 'ai-coach-chatbot-message-sent',
      properties: {
        source,
        conversationId,
      },
    });

    setHistory(previous => ({
      ...previous,
      messages: [...previous.messages, newMessage],
    }));

    await submitChatbotMessage(newMessage, {
      chat_model_id: chatModelId,
    });
  };

  const resubmitMessages: ConversationHistoryOutput['resubmitMessages'] =
    async () => {
      const latestMessage = history.messages[history.messages.length - 1];
      const canResubmitLatestMessage =
        latestMessage?.role === ChatbotMessageRole.user;

      // A failed command message is not in history, so retry re-sends the
      // stored command instead of whatever the conversation happens to end with.
      if (pendingCommandMessage && !canResubmitLatestMessage) {
        await submitChatbotMessage(pendingCommandMessage, {
          chat_model_id: chatModelId,
        });

        return;
      }

      // Retry from the last user message, so an assistant message is never
      // re-sent as if the user wrote it.
      const lastUserMessageIndex = findLastUserMessageIndex(history.messages);

      if (lastUserMessageIndex === -1) {
        logException(
          new Error(
            `No message to resubmit. Conversation ID: ${conversationId}`,
          ),
          {
            tags: {
              product: 'nova',
              feature: 'nova-chat',
              action: 'resubmit-messages',
              errorType: 'invalid-state',
            },
            extra: {
              conversationId,
              messageCount: history.messages.length,
            },
          },
        );

        return;
      }

      await submitChatbotMessage(history.messages[lastUserMessageIndex], {
        chat_model_id: chatModelId,
      });
    };

  const loadMoreMessages: ConversationHistoryOutput['loadMoreMessages'] =
    async () => {
      if (!history.hasPrevious || !history.startCursor || history.isLoading) {
        return;
      }

      onConversationUpdated?.('LOAD_MORE');
      await fetchConversationHistory({
        amount: CONVERSATION_CHUNK_SIZE,
        before: history.startCursor,
      });
    };

  const retryLoadingHistory: ConversationHistoryOutput['retryLoadingHistory'] =
    async () => {
      if (history.messages.length === 0) {
        onConversationUpdated?.('LOAD_MORE');
      }

      await fetchConversationHistory({
        amount: CONVERSATION_CHUNK_SIZE,
        before: history.startCursor,
      });
    };

  const handleAcknowledgeTransparencyCheck = () => {
    setTransparencyCheckRequired(false);
  };

  const clearConversationHistory = () => {
    // Require resetting the transparency check BEFORE emptying messages.
    // These two updates can be applied in separate renders (clearing runs from a
    // promise continuation, so React 17 does not batch them). Setting the flag first
    // ensures the follow-up effect never observes an empty conversation while
    // the transparency check is still not required — which would fire a
    // follow-up before the user re-acknowledges the disclaimer.
    setTransparencyCheckRequired(true);
    setHistory({
      isLoading: false,
      hadError: false,
      messages: [],
      hasPrevious: false,
    });
    // Clear transient submit/stream state too, so a mid-flight submit or a
    // visible retry/error from the previous conversation doesn't leak into the
    // fresh one once the transparency check is re-acknowledged.
    setIsSubmittingMessage(false);
    setIsStreaming(false);
    setIsSubmittingError(false);
    setPendingCommandMessage(null);
  };

  return {
    messages: history.messages,
    conversationId,
    isLoadingHistory: history.isLoading,
    isSubmittingMessage: isSubmittingMessage,
    isStreamingMessage: isStreaming,
    hadErrorSubmittingMessage: isSubmittingError,
    hadErrorLoadingHistory: mapHistoryErrorType(
      history.hadError,
      featureUnavailable,
      userSessionExpired,
    ),
    transparencyCheckRequired,
    submitMessage,
    resubmitMessages,
    loadMoreMessages,
    retryLoadingHistory,
    submitFollowUpMessage,
    submitUrlPromptMessage,
    handleAcknowledgeTransparencyCheck,
    clearConversationHistory,
  };
}
